// ═══════════════════════════════════════════════════════════════════════════
// TutorialRecorder — grabador in-app de tutoriales (Bloque 5 — 26-may-2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Modal que graba pantalla + micrófono + (opcional) cámara como burbuja
// overlay tipo Loom. Usa APIs nativas del navegador (getDisplayMedia,
// getUserMedia, MediaRecorder, Canvas), sin dependencias externas.
//
// FLUJO:
//   1. idle:       toggle cámara opcional, selector esquina, botón "Iniciar".
//   2. requesting: pide permisos del navegador (pantalla, mic, cámara si on).
//   3. recording:  graba con timer y auto-stop a 5 min.
//   4. stopped:    preview del video + form metadata.
//   5. uploading:  llama subirTutorial(...) con progress.
//   6. done:       muestra link al detalle.
//
// COMPOSICIÓN CON CÁMARA:
//   - Sin cámara: stream = display.video + mic.audio. Directo.
//   - Con cámara: <canvas> dibuja cada frame del display + cara overlay
//     (círculo en una esquina elegible). canvas.captureStream() + mic.audio.
//     Web Audio NO se mezcla con audio del sistema — solo mic, como cerró
//     Mariano en el diseño (opción A).
//
// CLEANUP CRÍTICO:
//   Todos los streams se detienen al cerrar el modal o cambiar de estado.
//   Sin esto, queda la luz roja del navegador indicando que la app está
//   grabando "en sombra".
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react'
import { subirTutorial, MODULOS_TUTORIAL, DURACION_MAX_SEG, MAX_TITULO, MAX_DESCRIPCION, MAX_TAGS, MAX_TAG } from '../lib/tutoriales'

const ESQUINAS = [
  { id: 'bottom-right', label: 'Abajo derecha' },
  { id: 'bottom-left',  label: 'Abajo izquierda' },
  { id: 'top-right',    label: 'Arriba derecha' },
  { id: 'top-left',     label: 'Arriba izquierda' },
]

// Bitrates conservadores. 1 Mbps video + 96 kbps audio = ~7 MB/min.
const VIDEO_BITS_PER_SEC = 1_000_000
const AUDIO_BITS_PER_SEC = 96_000

// Tamaño de la burbuja de cámara (px sobre el canvas de display, NO del DOM).
const BURBUJA_DIAMETRO = 180
const BURBUJA_MARGEN = 24

export default function TutorialRecorder({ open, onClose, onSubido }) {
  // ─── Estado del flujo ────────────────────────────────────────────────────
  const [estado, setEstado] = useState('idle') // idle|requesting|recording|stopped|uploading|done|error
  const [error, setError] = useState(null)
  const [incluirCamara, setIncluirCamara] = useState(true)
  const [esquinaCamara, setEsquinaCamara] = useState('bottom-right')
  const [segundos, setSegundos] = useState(0)
  const [videoBlob, setVideoBlob] = useState(null)
  const [thumbnailBlob, setThumbnailBlob] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [progreso, setProgreso] = useState(0)
  const [tutorialId, setTutorialId] = useState(null)

  // Form metadata (se completa en estado 'stopped')
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [modulo, setModulo] = useState('general')
  const [tagsRaw, setTagsRaw] = useState('')

  // ─── Refs para los streams y MediaRecorder ───────────────────────────────
  const displayStreamRef = useRef(null)
  const micStreamRef = useRef(null)
  const camStreamRef = useRef(null)
  const composedStreamRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])
  const canvasRef = useRef(null)           // canvas de composición (oculto)
  const displayVideoRef = useRef(null)     // <video> oculto con stream display
  const camVideoRef = useRef(null)         // <video> oculto con stream cámara
  const animationFrameRef = useRef(null)
  const timerRef = useRef(null)
  const previewVideoRef = useRef(null)

  // ─── Cleanup: detener TODO ───────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop() } catch (_) { /* graceful */ }
    }
    mediaRecorderRef.current = null
    chunksRef.current = []
    // Detener todos los tracks de todos los streams
    for (const stream of [displayStreamRef.current, micStreamRef.current, camStreamRef.current, composedStreamRef.current]) {
      if (stream) {
        try { stream.getTracks().forEach(t => t.stop()) } catch (_) {}
      }
    }
    displayStreamRef.current = null
    micStreamRef.current = null
    camStreamRef.current = null
    composedStreamRef.current = null
  }, [])

  // Cleanup al cerrar modal
  useEffect(() => {
    if (!open) {
      cleanup()
      setEstado('idle')
      setError(null)
      setSegundos(0)
      setVideoBlob(null)
      setThumbnailBlob(null)
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null) }
      setProgreso(0)
      setTitulo('')
      setDescripcion('')
      setModulo('general')
      setTagsRaw('')
      setTutorialId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Cleanup al desmontar el componente (último resort)
  useEffect(() => () => cleanup(), [cleanup])

  // ─── Iniciar grabación ───────────────────────────────────────────────────
  async function iniciarGrabacion() {
    setError(null)
    setEstado('requesting')
    try {
      // 1. Pantalla — el navegador muestra el diálogo nativo (pantalla/ventana/pestaña).
      //    audio:false porque NO grabamos audio del sistema (opción A — solo mic).
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15 },
        audio: false,
      })
      displayStreamRef.current = display

      // Si el user detiene desde el control del navegador (ej. clic en "Dejar de
      // compartir" del browser), detenemos también la grabación.
      display.getVideoTracks()[0].addEventListener('ended', () => {
        if (mediaRecorderRef.current?.state === 'recording') {
          detenerGrabacion()
        }
      })

      // 2. Micrófono — siempre obligatorio (decisión de diseño).
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      micStreamRef.current = mic

      // 3. Cámara — opcional, opt-in del toggle.
      let cam = null
      if (incluirCamara) {
        try {
          cam = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { width: 320, height: 240, frameRate: 15 },
          })
          camStreamRef.current = cam
        } catch (err) {
          // Si falla cámara, seguimos sin ella (graceful)
          console.warn('[TutorialRecorder] cámara falló, sigo sin overlay:', err.message)
          cam = null
        }
      }

      // 4. Componer el stream final
      const finalStream = cam
        ? await componerConCamara(display, cam, mic, esquinaCamara)
        : combinarStreams(display, mic)
      composedStreamRef.current = finalStream

      // 5. MediaRecorder
      let mimeType = 'video/webm;codecs=vp9,opus'
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8,opus'
        if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm'
      }
      const rec = new MediaRecorder(finalStream, {
        mimeType,
        videoBitsPerSecond: VIDEO_BITS_PER_SEC,
        audioBitsPerSecond: AUDIO_BITS_PER_SEC,
      })
      mediaRecorderRef.current = rec
      chunksRef.current = []
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data) }
      rec.onstop = () => onStopRecorder(mimeType)
      rec.onerror = (e) => {
        console.error('[TutorialRecorder] MediaRecorder error', e)
        setError('Error durante la grabación. Reintentá.')
        setEstado('error')
      }
      rec.start(1000) // un chunk por segundo para reducir riesgo de pérdida

      // 6. Timer + auto-stop
      setSegundos(0)
      timerRef.current = setInterval(() => {
        setSegundos(s => {
          const next = s + 1
          if (next >= DURACION_MAX_SEG) {
            detenerGrabacion()
            return next
          }
          return next
        })
      }, 1000)

      setEstado('recording')

      // 7. Capturar thumbnail después de 1 segundo (no en el instante 0 — esos
      //    suelen ser frames sin contenido todavía)
      setTimeout(() => {
        try {
          capturarThumbnail(cam ? canvasRef.current : displayVideoRef.current)
        } catch (err) {
          console.warn('[TutorialRecorder] thumbnail falló (graceful):', err.message)
        }
      }, 1500)

    } catch (err) {
      cleanup()
      // Si el user canceló el diálogo de permiso, el browser tira NotAllowedError
      // o AbortError. Mostramos un mensaje amable, no es un crash.
      const msg = err.name === 'NotAllowedError'
        ? 'Permiso denegado. Tenés que aceptar el acceso a la pantalla y al micrófono.'
        : err.name === 'NotFoundError'
        ? 'No se encontró el dispositivo requerido (mic o cámara).'
        : `Error iniciando la grabación: ${err.message}`
      setError(msg)
      setEstado(err.name === 'NotAllowedError' ? 'idle' : 'error')
    }
  }

  // ─── Componer canvas con cámara overlay ──────────────────────────────────
  async function componerConCamara(displayStream, camStream, micStream, esquina) {
    // Setup <video> ocultos con cada stream para poder dibujarlos al canvas
    const displayVideo = displayVideoRef.current
    const camVideo = camVideoRef.current
    displayVideo.srcObject = displayStream
    camVideo.srcObject = camStream
    await Promise.all([
      new Promise(res => { displayVideo.onloadedmetadata = () => { displayVideo.play(); res() } }),
      new Promise(res => { camVideo.onloadedmetadata = () => { camVideo.play(); res() } }),
    ])

    // El canvas matchea el tamaño del display capturado
    const canvas = canvasRef.current
    const dispW = displayVideo.videoWidth || 1280
    const dispH = displayVideo.videoHeight || 720
    canvas.width = dispW
    canvas.height = dispH
    const ctx = canvas.getContext('2d')

    // Coordenadas de la burbuja según la esquina
    function getBurbujaPos() {
      const r = BURBUJA_DIAMETRO / 2
      const m = BURBUJA_MARGEN
      switch (esquina) {
        case 'bottom-left':  return { cx: r + m, cy: dispH - r - m }
        case 'top-right':    return { cx: dispW - r - m, cy: r + m }
        case 'top-left':     return { cx: r + m, cy: r + m }
        case 'bottom-right': default: return { cx: dispW - r - m, cy: dispH - r - m }
      }
    }

    function draw() {
      // Fondo: el frame actual del display
      ctx.drawImage(displayVideo, 0, 0, dispW, dispH)
      // Burbuja: clip circular + cara
      const { cx, cy } = getBurbujaPos()
      const r = BURBUJA_DIAMETRO / 2
      ctx.save()
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.closePath()
      ctx.clip()
      // Mantener aspect ratio de la cámara (suele ser 4:3 a 320x240)
      const camW = camVideo.videoWidth || 320
      const camH = camVideo.videoHeight || 240
      const scale = Math.max(BURBUJA_DIAMETRO / camW, BURBUJA_DIAMETRO / camH)
      const drawW = camW * scale
      const drawH = camH * scale
      ctx.drawImage(camVideo, cx - drawW / 2, cy - drawH / 2, drawW, drawH)
      ctx.restore()
      // Borde sutil de la burbuja
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255,255,255,0.8)'
      ctx.lineWidth = 4
      ctx.stroke()

      animationFrameRef.current = requestAnimationFrame(draw)
    }
    draw()

    // Stream del canvas + audio del mic
    const canvasStream = canvas.captureStream(15)
    const audioTrack = micStream.getAudioTracks()[0]
    if (audioTrack) canvasStream.addTrack(audioTrack)
    return canvasStream
  }

  // Combina display.video + mic.audio sin cámara (caso simple)
  function combinarStreams(displayStream, micStream) {
    const stream = new MediaStream()
    displayStream.getVideoTracks().forEach(t => stream.addTrack(t))
    micStream.getAudioTracks().forEach(t => stream.addTrack(t))
    return stream
  }

  // ─── Capturar thumbnail desde un <video> o <canvas> ──────────────────────
  function capturarThumbnail(source) {
    if (!source) return
    const tmpCanvas = document.createElement('canvas')
    const w = source.videoWidth || source.width || 1280
    const h = source.videoHeight || source.height || 720
    // Reducimos la resolución de la thumbnail a max 640 de ancho para que pese menos
    const scale = Math.min(1, 640 / w)
    tmpCanvas.width = Math.round(w * scale)
    tmpCanvas.height = Math.round(h * scale)
    const tctx = tmpCanvas.getContext('2d')
    tctx.drawImage(source, 0, 0, tmpCanvas.width, tmpCanvas.height)
    tmpCanvas.toBlob((blob) => { if (blob) setThumbnailBlob(blob) }, 'image/jpeg', 0.85)
  }

  // ─── Detener grabación ───────────────────────────────────────────────────
  function detenerGrabacion() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }

  function onStopRecorder(mimeType) {
    const blob = new Blob(chunksRef.current, { type: mimeType })
    chunksRef.current = []
    setVideoBlob(blob)
    const url = URL.createObjectURL(blob)
    setPreviewUrl(url)
    // Detener los streams de captura (mantenemos el blob, ya no necesitamos las tracks)
    for (const stream of [displayStreamRef.current, micStreamRef.current, camStreamRef.current, composedStreamRef.current]) {
      if (stream) try { stream.getTracks().forEach(t => t.stop()) } catch (_) {}
    }
    setEstado('stopped')
  }

  // ─── Subir ───────────────────────────────────────────────────────────────
  async function handleSubir() {
    if (!videoBlob) return
    if (!titulo.trim()) { setError('Título requerido'); return }
    // Los tags no tienen maxLength posible (es un solo input separado por comas):
    // se avisa acá con el mismo tope que el backend rebota por forma (antes se
    // recortaban en silencio del lado del server).
    const tags = tagsRaw.split(',').map(t => t.trim()).filter(Boolean)
    if (tags.length > MAX_TAGS) { setError(`Hasta ${MAX_TAGS} tags`); return }
    if (tags.some(t => t.length > MAX_TAG)) { setError(`Cada tag hasta ${MAX_TAG} caracteres`); return }
    setEstado('uploading')
    setError(null)
    setProgreso(0)
    try {
      const res = await subirTutorial(
        { titulo: titulo.trim(), descripcion: descripcion.trim() || null, modulo, tags },
        videoBlob,
        { duracionSeg: segundos, thumbnailBlob, onProgress: setProgreso }
      )
      setTutorialId(res.id)
      setEstado('done')
      if (typeof onSubido === 'function') onSubido(res.id)
    } catch (err) {
      setError(err.message)
      setEstado('stopped')
    }
  }

  if (!open) return null

  // ─── Estructura del render — UN SOLO árbol JSX ────────────────────────────
  // Importante: los <video> y <canvas> ocultos (al final) viven como hermanos
  // constantes de los condicionales — React los mantiene en el DOM aún
  // cuando alternamos entre el modal centrado y el widget compacto. Si los
  // moviéramos a un branch condicional o cambiáramos de return entre estados,
  // se desmontarían y los refs perderían el DOM real, cortando los streams
  // conectados a `srcObject` mid-grabación.
  //
  // Estados:
  //   idle / requesting / stopped / uploading / done / error → modal centrado
  //                                                            con overlay oscuro.
  //   recording → SOLO un widget compacto en bottom-left, SIN overlay, para
  //               que el user pueda ver la pantalla que está grabando.
  return (
    <>
      {/* (a) Modal centrado — todos los estados menos recording */}
      {estado !== 'recording' && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[90] flex items-center justify-center p-4"
          onClick={estado === 'uploading' ? undefined : onClose}
        >
        <div
          className="bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">🎬</span>
            <h2 className="text-base font-bold text-white">
              {estado === 'recording' ? 'Grabando…' : estado === 'stopped' ? 'Preview y guardar' : estado === 'done' ? '✓ Listo' : 'Grabar tutorial'}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={estado === 'recording' || estado === 'uploading'}
            className="text-zinc-500 hover:text-white transition-colors disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Estado: IDLE — configuración previa */}
          {estado === 'idle' && (
            <>
              <p className="text-sm text-zinc-400">
                Vas a grabar tu pantalla con tu voz. Cuando apretes "Iniciar grabación",
                el navegador te va a pedir permiso para compartir pantalla y micrófono.
              </p>
              <div className="space-y-3 bg-white/[0.02] border border-white/10 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={incluirCamara} onChange={e => setIncluirCamara(e.target.checked)} />
                  <span className="text-sm text-zinc-200">📷 Incluir cámara como burbuja overlay</span>
                </label>
                {incluirCamara && (
                  <div className="pl-6">
                    <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Esquina de la burbuja</label>
                    <select
                      value={esquinaCamara}
                      onChange={e => setEsquinaCamara(e.target.value)}
                      className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-sm text-white"
                    >
                      {ESQUINAS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 bg-black/30 rounded-lg p-3">
                Hard limit: <strong>{Math.floor(DURACION_MAX_SEG / 60)} minutos</strong>. Pasado ese tiempo, la grabación se detiene automáticamente.
              </div>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200">{error}</div>
              )}
            </>
          )}

          {/* Estado: REQUESTING — esperando permisos */}
          {estado === 'requesting' && (
            <div className="py-8 text-center text-zinc-400 text-sm">
              Esperando permisos del navegador…
              <div className="text-[11px] text-zinc-600 mt-1">Aceptá los diálogos para continuar.</div>
            </div>
          )}

          {/* Estado: RECORDING — timer */}
          {estado === 'recording' && (
            <div className="py-8 text-center">
              <div className="inline-flex items-center gap-2 text-red-400 mb-2">
                <span className="inline-block w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="font-mono text-2xl">{fmtTiempo(segundos)}</span>
                <span className="text-xs text-zinc-500">/ {fmtTiempo(DURACION_MAX_SEG)}</span>
              </div>
              <p className="text-sm text-zinc-400">
                Estás grabando. Cuando termines, apretá "Detener" o cerrá desde el control del navegador.
              </p>
            </div>
          )}

          {/* Estado: STOPPED — preview + form */}
          {estado === 'stopped' && (
            <>
              {previewUrl && (
                <video ref={previewVideoRef} src={previewUrl} controls className="w-full max-h-64 bg-black rounded-lg" />
              )}
              <div className="text-[11px] text-zinc-500">
                Duración: <strong className="text-zinc-300">{fmtTiempo(segundos)}</strong>
                {videoBlob && <> · Tamaño: <strong className="text-zinc-300">{(videoBlob.size / 1024 / 1024).toFixed(1)} MB</strong></>}
                {thumbnailBlob && <> · Thumbnail: ✓</>}
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Título *</label>
                <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={MAX_TITULO} placeholder="Ej: Cómo conectar una cuenta ML manualmente" className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Descripción</label>
                <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={3} maxLength={MAX_DESCRIPCION} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Módulo</label>
                  <select value={modulo} onChange={e => setModulo(e.target.value)} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                    {MODULOS_TUTORIAL.map(m => <option key={m.codigo} value={m.codigo}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Tags (coma)</label>
                  <input type="text" value={tagsRaw} onChange={e => setTagsRaw(e.target.value)} placeholder="sincronizar, ml" className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                </div>
              </div>
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200">{error}</div>}
            </>
          )}

          {/* Estado: UPLOADING — progress */}
          {estado === 'uploading' && (
            <div className="py-6 space-y-2">
              <div className="text-sm text-zinc-300">Subiendo… {Math.round(progreso)}%</div>
              <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                <div className="bg-brand-accent h-full transition-all" style={{ width: `${progreso}%` }} />
              </div>
            </div>
          )}

          {/* Estado: DONE */}
          {estado === 'done' && (
            <div className="py-6 text-center">
              <div className="text-4xl mb-2">✓</div>
              <p className="text-sm text-zinc-200 mb-3">Tutorial publicado.</p>
              {tutorialId && (
                <a href={`/tutoriales/${tutorialId}`} className="text-brand-accent hover:underline text-sm">
                  Abrir el tutorial →
                </a>
              )}
            </div>
          )}

          {/* Estado: ERROR */}
          {estado === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-200">
              {error || 'Error desconocido. Cerrá y volvé a intentar.'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2">
          {estado === 'idle' && (
            <>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-zinc-300 hover:bg-white/[0.1] transition-colors">Cancelar</button>
              <button onClick={iniciarGrabacion} className="px-4 py-2 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-black text-sm font-semibold transition-colors">🔴 Iniciar grabación</button>
            </>
          )}
          {estado === 'recording' && (
            <button onClick={detenerGrabacion} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors">⏹ Detener</button>
          )}
          {estado === 'stopped' && (
            <>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-zinc-300 hover:bg-white/[0.1] transition-colors">Descartar</button>
              <button onClick={handleSubir} disabled={!titulo.trim()} className="px-4 py-2 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-black text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Subir tutorial</button>
            </>
          )}
          {(estado === 'done' || estado === 'error') && (
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-black text-sm font-semibold transition-colors">Cerrar</button>
          )}
        </div>

        </div>
        </div>
      )}

      {/* (b) Widget compacto — SOLO durante recording. Sin overlay, en
          bottom-left para no tapar la pantalla que se está grabando. */}
      {estado === 'recording' && (
        <div className="fixed bottom-4 left-4 z-[90] bg-zinc-900/95 backdrop-blur-sm border border-white/10 rounded-xl shadow-2xl px-3 py-2 flex items-center gap-3">
          <span className="inline-block w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
          <span className="font-mono text-sm text-white">{fmtTiempo(segundos)}</span>
          <span className="text-[10px] text-zinc-500">/ {fmtTiempo(DURACION_MAX_SEG)}</span>
          <button
            onClick={detenerGrabacion}
            className="ml-1 px-2.5 py-1 rounded-md bg-red-500 hover:bg-red-600 text-white text-xs font-semibold transition-colors"
            title="Detener grabación"
          >
            ⏹ Detener
          </button>
        </div>
      )}

      {/* (c) Hidden elements — SIEMPRE presentes mientras open=true. Viven
          como hermanos constantes de los condicionales (a) y (b) para que
          React no los desmonte al alternar entre estados. */}
      <div className="hidden">
        <video ref={displayVideoRef} muted playsInline />
        <video ref={camVideoRef} muted playsInline />
        <canvas ref={canvasRef} />
      </div>
    </>
  )
}

function fmtTiempo(seg) {
  const m = Math.floor(seg / 60)
  const s = Math.floor(seg % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
